import React, { useCallback, useMemo, useState, Component } from "react";
import Box from "@carrier-io/air-react/Box";
import {
  Modal,
  ModalActions,
  ModalContent,
  Typography,
} from "@carrier-io/air-react";
import Button from "@carrier-io/air-react/Button";
import DragDropUpload, {
  FileItem,
} from "@carrier-io/air-react/patterns/Upload/DragDropUpload";
import { useBulkUsersCsvParser } from "../../../hooks/useBulkUsersCsvParser";
import { BulkAddUsersModal } from "./BulkAddUsersModal";
import UploadedFile from "@carrier-io/air-react/patterns/UploadedFile";
import { Clients, useQuery } from "../../../apollo";
import { GetAllRolesQuery } from "../../../graphql/queries/getAllRoles";
import { GetAllGroupsQuery } from "../../../graphql/queries/getAllGroups";
import { ExtendedUserInput } from "../../../generated/ups/graphql";
import { ValidationError } from "class-validator";
import { useNavigate } from "react-router-dom";
import { RoutesPage } from "../../../constants/routes";
import { ParsersContentError } from "./ParsersContentError";

type ErrorHandlerProps = { clear: () => unknown };

class ErrorBoundaryWrapper extends Component<{
  children: React.ReactNode;
  onError: () => React.ReactNode;
}> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.onError();
    }

    return this.props.children;
  }
}

const ErrorHandler: React.FC<ErrorHandlerProps> = ({ clear }) => {
  return (
    <Modal open={true}>
      <ModalContent>
        <Typography
          variant="h5"
          sx={{ textAlign: "center", marginTop: "24px" }}
        >
          Something went wrong
        </Typography>
      </ModalContent>
      <ModalActions>
        <Button variant="contained" onClick={clear}>
          Close
        </Button>
      </ModalActions>
    </Modal>
  );
};

export const UploadUserFile: React.FC = () => {
  const navigate = useNavigate();
  const [fileName, setFileName] = useState<string | null>();
  const [showImportModal, setShowImportModal] = useState(false);
  const { data: roles, loading: isRolesLoading } = useQuery<{
    getAllRoles: Array<string>;
  }>(Clients.UPS, GetAllRolesQuery);

  const { data: groups, loading: isGroupsLoading } = useQuery<{
    getAllGroups: Array<string>;
  }>(Clients.UPS, GetAllGroupsQuery);

  const validateRoles = useCallback(
    (user: ExtendedUserInput) => {
      if (isRolesLoading) {
        return null;
      }

      const inputRoles = user.roles;
      const validRoles = roles?.getAllRoles || [];

      const invalidRoles = (inputRoles || []).filter(
        (role) => !validRoles.includes(role),
      );

      if (invalidRoles.length > 0) {
        const validationError = new ValidationError();
        const errors: ValidationError = {
          property: "roles",
          target: user,
          value: invalidRoles.join(", "),
          constraints: {
            isValidRole: `Roles do not exist.`,
          },
          children: [],
        };

        return Object.assign(validationError, errors);
      }

      return null;
    },
    [isRolesLoading, roles?.getAllRoles],
  );

  const validateGroups = useCallback(
    (user: ExtendedUserInput) => {
      if (isGroupsLoading) {
        return null;
      }

      const inputGroups = user.groups;
      const validGroups = groups?.getAllGroups || [];

      const invalidGroups = (inputGroups || []).filter(
        (group) => !validGroups.includes(group),
      );

      if (invalidGroups.length > 0) {
        const validationError = new ValidationError();
        const errors: ValidationError = {
          property: "groups",
          target: user,
          value: invalidGroups.join(", "),
          constraints: {
            isValidGroup: `Groups do not exist.`,
          },
          children: [],
        };

        return Object.assign(validationError, errors);
      }

      return null;
    },
    [groups?.getAllGroups, isGroupsLoading],
  );

  const validations = useMemo(
    () => [validateRoles, validateGroups],
    [validateRoles, validateGroups],
  );

  const { isParsing, progress, users, isError, errors, consume, cancel } =
    useBulkUsersCsvParser(validations);

  const uploadHandler = (fileItems: FileItem[]) => {
    const fileItem = fileItems.shift();

    if (!fileItem) {
      return;
    }

    const { fileData: file } = fileItem;

    if (!file) {
      return;
    }

    setFileName(file.name);
    if (isParsing) {
      cancel(); //we only allow one file at a time
    }
    consume(file);
  };

  const cancelHandler = () => {
    navigate(RoutesPage.UserManagement);
  };

  const deleteHandler = () => {
    setFileName(null);
    cancel();
  };

  return (
    <ErrorBoundaryWrapper
      onError={() => <ErrorHandler clear={cancelHandler} />}
    >
      <DragDropUpload
        sx={{
          padding: "10px",
        }}
        size={"inherit"}
        fileItems={[]}
        acceptType={[".csv"]}
        onUpload={uploadHandler}
        multiple={false}
        itemsToDisplay={0}
      ></DragDropUpload>
      <Box marginTop="20px">
        {fileName ? (
          <Box>
            <UploadedFile
              extension="csv"
              fileName={fileName.split(".")[0]}
              progress={progress}
              isLoading={isParsing}
              isInvalid={isError}
              onDelete={deleteHandler}
              isSuccess={progress === 100 && !isError}
              wrapperSxProps={{
                border: "none",
              }}
            />
            {errors.file?.length ? (
              <Box marginTop="16px" marginLeft="32px">
                {errors.file.map((error) => (
                  <Typography
                    key={error.message}
                    variant="body2"
                    color="error.main"
                  >
                    {error.message}
                  </Typography>
                ))}
              </Box>
            ) : null}
            {errors.data?.length ? (
              <Box marginTop="16px" marginLeft="32px">
                {errors.data.map((error) => (
                  <ParsersContentError key={error.lineNumber} error={error} />
                ))}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Box
        display="flex"
        marginTop="24px"
        paddingTop="24px"
        justifyContent="space-between"
        borderTop="1px solid #DCDEE0"
      >
        <Button variant="text" onClick={cancelHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => setShowImportModal(true)}
          disabled={isParsing || progress < 100 || isError}
        >
          Continue
        </Button>
      </Box>
      <BulkAddUsersModal
        open={showImportModal}
        users={users}
        onClose={() => setShowImportModal(false)}
        onContinue={() => {
          setShowImportModal(false);
          navigate(RoutesPage.UserManagement);
        }}
      />
    </ErrorBoundaryWrapper>
  );
};
